import {
    Avatar, Box, Button,
    Card, Checkbox,
    DialogActions,
    DialogTitle,
    Divider,
    Grid,
    Modal,
    ModalClose,
    ModalDialog,
    Stack, Switch,
    Typography, useTheme
} from "@mui/joy";
import React, {useEffect, useState} from "react";
import {
    AlertCircle,
    Check,
    CheckCircle2,
    ChevronLeftIcon,
    ChevronRightIcon,
    CircleAlertIcon,
    Clock, Dot, Globe, Globe2, Mic, Monitor, Mouse, MousePointer, TextCursor, Wifi, X
} from "lucide-react";
import {Test} from "../../models/test";
import {applyTransparency} from "../../theme";
import {formatTimeFromSeconds, formatTimeFromSeconds2} from "../../utils/formatTime";
import {EXAM_DURATION_MAPPER} from "../../models/exam";
import {useTestRoom} from "../../hooks/useTestRoom";
import {useTranslation} from "react-i18next";
import {useBreakpoint} from "../../hooks/useBreakPoint";
import {defaultTestTime} from "../../utils/defaultTestTime";

type PreTestInstructionProps = {
    open?: boolean,
    onClose?: (config?: {
        timerEnabled?: boolean,
    }) => void,
    test?: Test,
    questionCount?: number,
    time?: number,
}

type GeneralInstructionProps = {
    test: Test,
    questionCount: number,
    time: number,
    timerEnabled: boolean,
    setTimerEnabled: (enabled: boolean) => void,
}

const PreTestInstructionsDialog = (props: PreTestInstructionProps) => {
    const theme = useTheme()
    const testRoom = useTestRoom()
    const breakpoint = useBreakpoint()
    const {t} = useTranslation()
    const [currentSlide, setCurrentSlide] = useState(0)
    const [canSkip, setCanSkip] = useState(false)
    const slides = [<GeneralInstructionsComponent setTimerEnabled={testRoom.setTimerEnabled}
                                                  timerEnabled={testRoom.timerEnabled}
                                                  test={props.test} questionCount={props.questionCount}
                                                  time={props.time}/>,
        <NavigationAndGuidelinesComponent/>, <SystemRequirementsComponent/>]

    const nextSlide = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1)
        } else
            props.onClose?.()
    }

    const prevSlide = () => {
        setCurrentSlide(Math.max(0, currentSlide - 1))
    }

    useEffect(() => {
        setCanSkip(JSON.parse(localStorage.getItem("canSkipTestInstructions")))
    }, [])

    useEffect(() => {
        if (props.open)
            localStorage.setItem("canSkipTestInstructions", "true")
    }, [props.open])

    return <Modal open={props.open} onClose={(event, reason) => {
        if (reason === "closeClick")
            props.onClose?.()
    }}>
        <ModalDialog variant="outlined" layout={breakpoint.isXs ? "fullscreen" : "center"} role="alertdialog"
                     size={"lg"}
                     sx={{gap: 0, minWidth: {xs: "100%", md: "40rem"}}}>
            {canSkip && <ModalClose/>}
            <Stack gap={2}>
                <DialogTitle sx={{alignItems: "center"}}>
                    <Typography level={"h4"}
                                startDecorator={<Avatar variant={"soft"} size={"md"} color={"secondary"}>
                                    <CircleAlertIcon/>
                                </Avatar>}>
                        {props.test.title}
                    </Typography>
                </DialogTitle>
                <Typography level={"body-sm"}>
                    {t("testInstructionsNotice")}
                </Typography>

                <Stack direction={"row"} gap={1} justifyContent={"center"} alignItems={"center"}>
                    {Array.of(...slides).map((value, index) =>
                        <Box onClick={() => setCurrentSlide(index)} width={index === currentSlide ? 30 : 10} height={10}
                             sx={{
                                 borderRadius: 25,
                                 cursor: "pointer",
                                 backgroundColor: index === currentSlide ? `secondary.400` : applyTransparency(theme.palette.secondary[500], 0.3)
                             }}/>
                    )}
                </Stack>

            </Stack>

            <Divider sx={{marginTop: 2}}/>


            {slides[currentSlide]}

            <Divider/>

            <Checkbox onChange={event => localStorage.setItem("skipPreTestInstructions", `${event.target.checked}`)}
                      color={"secondary"}
                      size={"sm"}
                      sx={{
                          paddingTop: 2,
                          alignSelf: "center",
                          alignItems: "center",
                      }}
                      label={<Typography level={"body-sm"}>
                          {t("hidePreTestInstructionsLabel")}
                      </Typography>}/>

            <DialogActions
                sx={{justifyContent: "space-between", alignItems: "center"}}>
                {currentSlide === slides.length - 1 ?
                    <Button color={"secondary"} size={"md"} onClick={() => props.onClose?.()}
                            startDecorator={<CheckCircle2/>}>
                        {t("startTestActionButtonLabel")}
                    </Button> :
                    <Button color={"secondary"} size={"md"} onClick={nextSlide} endDecorator={<ChevronRightIcon/>}>
                        {t("nextInstructionsPageActionButtonLabel")}
                    </Button>}

                <Typography textAlign={"center"} sx={{fontSize: "0.8rem"}}>
                    {t("instructionPageLabel", {page: currentSlide + 1, pageCount: slides.length})}
                </Typography>


                <Button onClick={prevSlide} disabled={currentSlide === 0} startDecorator={<ChevronLeftIcon/>}
                        variant={"outlined"} size={"md"}
                        color={"neutral"}>
                    {t("previousInstructionsPageActionButtonLabel")}
                </Button>
            </DialogActions>

        </ModalDialog>
    </Modal>
}

const GeneralInstructionsComponent = (props: GeneralInstructionProps) => {
    const testRoom = useTestRoom()
    const {t} = useTranslation()

    return <Stack gap={3} padding={1} sx={{overflowY: "scroll"}}>
        <Stack alignItems={"center"} justifyContent={"center"} width={"100%"}>
            <Typography level={"body-lg"} textAlign={{xs: "center"}}>
                {t("generalInstructionPageTitle")}
            </Typography>

            <Typography level={"body-sm"} textAlign={{xs: "center"}}>
                {t("generalInstructionPageDescription")}
            </Typography>
        </Stack>

        <Grid container spacing={2}>
            <Grid md={6} xs={12}>
                <Card data-no-glow data-no-animation
                      sx={{alignItems: "center", justifyContent: "center", minWidth: "10rem"}}>
                    <Typography
                        startDecorator={<Avatar color={"secondary"} variant={"soft"} sx={{borderRadius: 10}}
                                                size={"sm"}>
                            <CheckCircle2/>
                        </Avatar>}>
                        {t("totalQuestionCountLabel")}
                    </Typography>
                    <Typography level={"h2"}>
                        {props.questionCount}
                    </Typography>
                </Card>
            </Grid>

            <Grid md={6} xs={12}>
                <Card data-no-glow data-no-animation
                      sx={{alignItems: "center", justifyContent: "center", minWidth: "10rem"}}>
                    <Typography
                        startDecorator={<Avatar color={"secondary"} variant={"soft"} sx={{borderRadius: 10}}
                                                size={"sm"}>
                            <Clock/>
                        </Avatar>}>
                        {t("timeAllowedLabel")}
                    </Typography>

                    <Typography level={"h2"}>
                        {formatTimeFromSeconds2(defaultTestTime[props.test?.exam.type][props.test?.sections[0]] || 0)}
                    </Typography>
                </Card>
            </Grid>


        </Grid>


        <Card data-no-glow data-no-animation color={props.timerEnabled ? "secondary" : "neutral"}
              variant={props.timerEnabled ? "soft" : "outlined"}
              sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: props.test.mode === "mock" ? 0.5 : 1,
                  minWidth: "10rem"
              }}>

            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} width={"100%"}>

                <Stack gap={2} direction={"row"} alignItems={"center"}>
                    <Avatar color={"secondary"} variant={"soft"} sx={{borderRadius: 10}} size={"lg"}>
                        <Clock style={{fontSize: 30}}/>
                    </Avatar>

                    <Stack>
                        <Typography level={"title-md"}>
                            {t("enableTimerTitle")}
                        </Typography>

                        <Typography level={"body-sm"}>
                            {t("enableTimerDescription")}
                        </Typography>
                    </Stack>
                </Stack>


                <Switch color={props.timerEnabled ? "secondary" : "neutral"} variant={"outlined"} size={"lg"}
                        checked={props.timerEnabled}
                        onChange={event => props.setTimerEnabled(event.target.checked)}
                        disabled={props.test.mode === "mock"}
                        sx={{
                            '--Switch-thumbColor': "white"
                        }}/>

            </Stack>

            {props.test?.mode === "mock" &&
                <Typography level={"body-xs"} color={"secondary"}>
                    {t("mockTestDisableTimerNotice")}
                </Typography>}

        </Card>



        <Card data-no-glow data-no-animation color={"secondary"}
              sx={{flexDirection: "row", alignItems: "center", gap: 2}}>
            <AlertCircle style={{fontSize: 60}}/>
            <Typography level={"body-sm"}>
                {t("generalInstructionsProTipMessage")}
            </Typography>
        </Card>

    </Stack>
}

const NavigationAndGuidelinesComponent = () => {
    const {t} = useTranslation()
    return <Stack gap={3} padding={2} sx={{overflowY: "scroll"}}>
        <Stack alignItems={"center"} justifyContent={"center"} width={"100%"}>
            <Typography level={"body-lg"}>
                {t("navigationInstructionsPageTitle")}
            </Typography>

            <Typography level={"body-sm"} textAlign={{xs: "center"}}>
                {t("navigationInstructionsPageDescription")}
            </Typography>
        </Stack>

        <Grid container spacing={3}>
            <Grid md={6} xs={12}>
                <Card data-no-glow data-no-animation
                      sx={{minHeight: "14rem", justifyContent: "center", minWidth: "10rem"}}>

                    <Typography level={"title-lg"}
                                startDecorator={<Avatar sx={{borderRadius: 8}} size={"sm"} color={"secondary"}>
                                    <MousePointer/>
                                </Avatar>}>
                        {t("navigationInstructionsNavigationSectionTitle")}
                    </Typography>

                    <Typography startDecorator={<CheckCircle2/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            {t("navigationInstruction")}
                        </Typography>

                    </Typography>
                </Card>
            </Grid>

            <Grid md={6} xs={12}>
                <Card data-no-glow data-no-animation
                      sx={{minHeight: "14rem", justifyContent: "center", minWidth: "10rem"}}>
                    <Typography level={"title-lg"}
                                startDecorator={<Avatar sx={{borderRadius: 8}} size={"sm"} color={"success"}>
                                    <CheckCircle2/>
                                </Avatar>}>
                        {t("navigationInstructionsAnswersSectionTitle")}
                    </Typography>

                    <Typography startDecorator={<CheckCircle2/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            {t("answerInstruction1")}
                        </Typography>
                    </Typography>

                    <Typography startDecorator={<CheckCircle2/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            {t("answerInstruction2")}
                        </Typography>
                    </Typography>

                </Card>
            </Grid>

        </Grid>


        <Card data-no-glow data-no-animation color={"secondary"}
              sx={{flexDirection: "row", alignItems: "center", gap: 2}}>
            <AlertCircle style={{fontSize: 40}}/>
            <Stack>
                <Typography level={"body-md"}>
                    {t("submissionInstruction")}
                </Typography>
                <Typography level={"body-xs"}>
                    {t("submissionNotice")}
                </Typography>
            </Stack>
        </Card>
    </Stack>

}

const SystemRequirementsComponent = () => {
    const {t} = useTranslation()
    return <Stack gap={3} padding={2} sx={{overflowY: "scroll"}}>
        <Stack alignItems={"center"} justifyContent={"center"} width={"100%"}>
            <Typography level={"body-lg"}>
                {t("systemRequirementsTitle")}
            </Typography>

            <Typography level={"body-sm"}>
                {t("systemRequirementsDescription")}
            </Typography>
        </Stack>

        <Grid container spacing={3}>
            <Grid md={6} xs={12}>
                <Card data-no-glow data-no-animation
                      sx={{minHeight: "14rem", minWidth: "10rem"}}>

                    <Typography startDecorator={<Avatar sx={{borderRadius: 8}} size={"sm"} color={"secondary"}>
                        <Globe/>
                    </Avatar>}>
                        {t("systemRequirementsBrowserTitle")}
                    </Typography>

                    <Typography startDecorator={<Dot/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            Chrome ({t("systemRequirementRecommendedBrowserLabel")})
                        </Typography>
                    </Typography>

                    <Typography startDecorator={<Dot/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            Firefox
                        </Typography>
                    </Typography>

                    <Typography startDecorator={<Dot/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            Safari
                        </Typography>
                    </Typography>

                    <Typography startDecorator={<Dot/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            Microsoft Edge
                        </Typography>
                    </Typography>

                </Card>
            </Grid>

            <Grid md={6} xs={12}>
                <Card data-no-glow data-no-animation
                      sx={{minHeight: "14rem", justifyContent: "center", minWidth: "10rem"}}>

                    <Typography startDecorator={<Avatar sx={{borderRadius: 8}} size={"sm"} color={"secondary"}>
                        <Monitor/>
                    </Avatar>}>
                        {t("systemRequirementDisplayTitle")}
                    </Typography>

                    <Typography startDecorator={<Dot/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            {t("systemRequirementDisplayResolution")}
                        </Typography>
                    </Typography>

                    <Typography startDecorator={<Dot/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            {t("systemRequirementDisplayFullScreenRecommendation")}
                        </Typography>
                    </Typography>

                    <Typography startDecorator={<Dot/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            {t("systemRequirementDisplayZoomLevel")}
                        </Typography>
                    </Typography>

                    <Typography startDecorator={<Dot/>} color={"secondary"}>
                        <Typography textColor={"text.secondary"}>
                            {t("systemRequirementLighting")}
                        </Typography>
                    </Typography>

                </Card>
            </Grid>

        </Grid>

        <Card data-no-glow data-no-animation color={"secondary"}
              sx={{height: "max-content", alignItems: "center", justifyContent: "center", minWidth: "10rem"}}>
            <Avatar size={"lg"} color={"secondary"}>
                <CheckCircle2 style={{fontSize: 40}}/>
            </Avatar>

            <Typography level={"title-lg"}>
                {t("callToStartTestActionLabel")}
            </Typography>

            <Typography>
                {t("callToStartTestActionNotice")}
            </Typography>

            <Stack gap={1} width={"100%"} alignItems={"start"}>
                <Typography startDecorator={<CheckCircle2/>} color={"secondary"}>
                    <Typography textColor={"text.secondary"}>
                        {t("callToStartTestActionNotice1")}
                    </Typography>
                </Typography>
                <Typography startDecorator={<CheckCircle2/>} color={"secondary"}>
                    <Typography textColor={"text.secondary"}>
                        {t("callToStartTestActionNotice2")}
                    </Typography>
                </Typography>
                <Typography startDecorator={<CheckCircle2/>} color={"secondary"}>
                    <Typography textColor={"text.secondary"}>
                        {t("callToStartTestActionNotice3")}
                    </Typography>
                </Typography>

            </Stack>

        </Card>

    </Stack>
}

export default PreTestInstructionsDialog